import {lazy, Suspense, useContext} from 'react'

import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import AtomButton, {atomButtonShapes, atomButtonSizes} from '@s-ui/react-atom-button'
import {ATOM_ICON_SIZES} from '@s-ui/react-atom-icon'
import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import MoleculeNotification, {
  moleculeNotificationAutoClose,
  moleculeNotificationBorderSizes,
  moleculeNotificationTypes,
  moleculeNotificationVariations
} from '@s-ui/react-molecule-notification'
import VisuallyHidden from '@s-ui/react-primitive-visually-hidden'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import PhotoUploader from '@adv-mt/mt-form-publish-ad-photo-uploader'
import Layout from '@adv-mt/mt-layout-basic'
import IconTriangleWarningOutline from '@adv-mt/mt-svg-icons/lib/TriangleWarningOutline'

import {useUploadImages} from './useUploadImages.js'

const Loader = lazy(() => import(/* webpackChunkName: "PanelGenerateCommentLoader" */ './Loader.js'))

const BASE_CLASS_NAME = 'mt-PublishYourAd-stepUploadImages'

const StepUploadImages = ({adId, destinationUrl, fields, pageName}) => {
  const {i18n} = useContext(Context)
  const {user} = useContext(AuthenticationContext)

  const {
    config: {IMAGES_FIELD_ID, RECOMMENDED_NUMBER_OF_UPLOADED_IMAGES, URL_STATICS_IMAGES},
    handlers: {handleSubmit, handleUploadPhotosRejected, handleUploadPhotosSuccess},
    state: {isGeneratingComment, isLoading, numberOfImagesUploaded, showNotification},
    tracking: {trackOnClickPhotoUploader, trackOnImagesSelected}
  } = useUploadImages({adId, destinationUrl, fields, pageName})

  const areImagesUploaded = numberOfImagesUploaded > 0

  const buttonLiteral = areImagesUploaded
    ? `PUBLISH_AD.STEP_UPLOAD_PHOTOS.CONTINUE_WITH_N_PHOTOS`
    : `PUBLISH_AD.STEP_UPLOAD_PHOTOS.CONTINUE_WITHOUT_PHOTOS`
  const buttonLabel = i18n.t(buttonLiteral, numberOfImagesUploaded)

  return (
    <>
      {isGeneratingComment && (
        <Suspense fallback={<></>}>
          <Loader />
        </Suspense>
      )}

      <Layout.Container isFullWidth>
        <div className={BASE_CLASS_NAME}>
          <main className={`${BASE_CLASS_NAME}-main`}>
            <div>
              <h1 aria-hidden className={`${BASE_CLASS_NAME}Title`}>
                {i18n.t('PUBLISH_AD.PHOTO_UPLOADER.TITLE')}
              </h1>
            </div>

            <VisuallyHidden>
              <label htmlFor={IMAGES_FIELD_ID}>{i18n.t('PUBLISH_AD.PHOTO_UPLOADER.TITLE')}</label>
            </VisuallyHidden>

            <PhotoUploader
              adId={adId}
              callbackPhotosRejected={handleUploadPhotosRejected}
              callbackPhotosUploaded={handleUploadPhotosSuccess}
              id={IMAGES_FIELD_ID}
              initialPhotos={[]}
              onDropFiles={trackOnImagesSelected}
              onFileDialogOpen={trackOnClickPhotoUploader}
              userId={user?.userId}
            />

            {showNotification && (
              <MoleculeNotification
                autoClose={moleculeNotificationAutoClose.manual}
                effect={false}
                roundedCorners={moleculeNotificationBorderSizes.medium}
                showCloseButton={false}
                type={moleculeNotificationTypes.warning}
                variation={moleculeNotificationVariations.positive}
                icon={
                  <span className={`${BASE_CLASS_NAME}Notification`}>
                    <IconTriangleWarningOutline size={ATOM_ICON_SIZES.medium} />
                  </span>
                }
              >
                <p className={`${BASE_CLASS_NAME}Notification ${BASE_CLASS_NAME}NotificationText`}>
                  {i18n.t(`PUBLISH_AD.STEP_UPLOAD_PHOTOS.RECOMMENDATION`, {
                    recommendedNumber: RECOMMENDED_NUMBER_OF_UPLOADED_IMAGES
                  })}
                </p>
              </MoleculeNotification>
            )}

            <div className={`${BASE_CLASS_NAME}Button`}>
              <AtomButton
                fullWidth
                isLoading={isLoading}
                onClick={handleSubmit}
                shape={atomButtonShapes.CIRCULAR}
                size={atomButtonSizes.LARGE}
              >
                {buttonLabel}
              </AtomButton>
            </div>
          </main>
          <aside className={`${BASE_CLASS_NAME}-aside`}>
            <AtomImage
              aria-hidden="true"
              role="presentation"
              src={`${URL_STATICS_IMAGES}illustrations/choose-photo-order.svg`}
            />
          </aside>
        </div>
      </Layout.Container>
    </>
  )
}

StepUploadImages.displayName = 'StepUploadImages'

StepUploadImages.propTypes = {
  adId: PropTypes.string.isRequired,
  destinationUrl: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  pageName: PropTypes.string.isRequired
}

export default StepUploadImages
